/**
 * Ported from unmaintained https://github.com/souporserious/react-tether
 */
import { Children, Component, PropsWithChildren, ReactNode } from 'react'
import ReactDOM from 'react-dom'

import pickBy from 'lodash/pickBy'
import Tether, { ITetherOptions } from 'tether'

import type { Attachment } from './types'

interface Props {
  attachment: Attachment
  targetAttachment?: Attachment
  pin?: boolean
  renderElementTag: string
  offset?: string
  onUpdate?: () => void
  underModal?: boolean
  // Helps us avoid styling conflicts between tooltip and popover
  type?: 'tooltip' | 'popover'
}

class TetherComponent extends Component<PropsWithChildren<Props>> {
  targetNode: any = null
  elementParentNode: any = null
  tether: Tether | null | undefined = null
  static defaultProps = {
    renderElementTag: 'div',
  }

  constructor(props: PropsWithChildren<Props>) {
    super(props)
    this.createParentNode(props)
  }

  componentDidMount() {
    this.targetNode = ReactDOM.findDOMNode(this) // eslint-disable-line

    this.update()
    this.registerEventListeners()
  }

  UNSAFE_componentWillReceiveProps(nextProps: PropsWithChildren<Props>) {
    this.createParentNode(nextProps)
  }

  createParentNode(props: PropsWithChildren<Props>) {
    const { underModal, children, renderElementTag, type = 'popover' } = props
    const elements = Children.toArray(children)
    const modal = elements[1]

    if (!modal) return

    if (this.elementParentNode) return

    this.elementParentNode = document.createElement(renderElementTag)

    // Distinguishes between Popover and Tooltip to avoid styling collisions.
    this.elementParentNode.setAttribute('class', `factorial-${type}`)

    if (underModal) {
      this.elementParentNode.setAttribute('class', 'underModal')
    }

    if (document.body) {
      document.body.appendChild(this.elementParentNode)
    }
  }

  componentDidUpdate() {
    this.update()
  }

  componentWillUnmount() {
    this.destroy()
  }

  registerEventListeners() {
    if (!this.tether) return

    if (this.props.onUpdate) {
      ;(this.tether as any).on('update', this.props.onUpdate)
    }
  }

  destroy() {
    if (this.tether) {
      this.tether.destroy()
    }

    if (this.elementParentNode) {
      this.elementParentNode.parentNode.removeChild(this.elementParentNode)
    }

    this.elementParentNode = null
    this.tether = null
  }

  update() {
    const { children } = this.props
    const elements = Children.toArray(children)
    const modal = elements[1]

    // if no element component provided, bail out
    if (!modal) {
      // destroy Tether element if it has been created
      if (this.tether) {
        this.destroy()
      }

      return
    }

    this.updateTether()
  }

  updateTether() {
    const { offset = '0 0' } = this.props
    const options = pickBy(this.props, Boolean) as Partial<Props>

    // NOTE: Tether will throw if the target or element parent is not present
    if (!this.targetNode || !this.elementParentNode) return

    const tetherOptions: ITetherOptions = {
      target: this.targetNode,
      element: this.elementParentNode,
      offset,
      attachment: this.props.attachment,
      ...options,
      constraints: [
        {
          to: 'window',
          attachment: 'none',
          pin: this.props.pin ?? true,
        },
      ],
    }

    if (!this.tether) {
      this.tether = new Tether(tetherOptions)
    } else {
      this.tether.setOptions(tetherOptions)
    }

    if (this.tether) this.tether.position()
  }

  renderPortal(modal: ReactNode | null | undefined) {
    if (!this.elementParentNode) return null

    if (!modal) return null

    return ReactDOM.createPortal(modal, this.elementParentNode)
  }

  render() {
    const { children } = this.props
    const elements = Children.toArray(children)
    const target = elements[0]
    const modal = elements[1]

    return [target, this.renderPortal(modal)]
  }
}

export default TetherComponent
